import { supabase } from "@/services/supabase/client";

export type ProfileSortBy =
  | "display_name"
  | "created_at"
  | "gig_count"
  | "ww_count"
  | "favourite_count"
  | "dm_count"
  | "plan";
export type ProfileSortOrder = "asc" | "desc";

export interface Profile {
  id: string;
  email: string | null;
  phone: string | null;
  roles?: string[];
  created_at: string;
  display_name: string;
  gig_count: number;
  avatar_url?: string | null;
  ww_count?: number;
  favourite_count?: number;
  connection_count?: number;
  dm_count?: number;
  plan?: string | null;
}

export interface ProfilesResponse {
  items: Profile[];
  total: number;
  has_more: boolean;
}

interface GetAdminUserDetailResponse {
  ok: boolean;
  user: Profile | null;
}

export interface GetProfilesParams {
  limit: number;
  offset: number;
  search?: string | null | undefined;
  sortBy?: ProfileSortBy;
  sortOrder?: ProfileSortOrder;
}

const emptyResponse: ProfilesResponse = { items: [], total: 0, has_more: false };

const getProfiles = async ({
  limit,
  offset,
  search,
  sortBy,
  sortOrder,
}: GetProfilesParams): Promise<ProfilesResponse> => {
  const { data, error } = await supabase.rpc("get_admin_users", {
    _limit: limit,
    _offset: offset,
    ...(search ? { _query_text: search } : {}),
    ...(sortBy ? { _sort_by: sortBy } : {}),
    ...(sortOrder ? { _sort_order: sortOrder } : {}),
  });

  if (error) {
    console.error("getProfiles error:", error);
    return emptyResponse;
  }

  return data ?? emptyResponse;
};

const getProfileById = async (userId: string): Promise<Profile | null> => {
  const { data, error } = await supabase.rpc("get_admin_user_detail", {
    _user_id: userId,
  });

  if (error) {
    console.error("getProfileById error:", error);
    throw new Error(error.message || "Failed to fetch user");
  }

  const response = data as GetAdminUserDetailResponse | null;
  if (!response?.ok) {
    return null;
  }

  return response.user ?? null;
};

const ProfileService = { getProfiles, getProfileById };

export default ProfileService;
